import {
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Skeleton,
} from "@mui/material";
import { DataGrid, GridColDef, GridValidRowModel } from "@mui/x-data-grid";

import styled from "@emotion/styled";
import { useEffect, useState } from "react";

import type { FetchNextPageOptions } from "@tanstack/react-query";
import { DEFAULT_PAGE_SIZE, vibrant } from "@repo/dashboard-constants";
import { Button } from "@repo/dashboard-components";

const PaginationWrapper = styled.div<{ height: string | number }>`
  position: relative;
  height: ${(props) => props.height};
  width: 100%;
`;

const PaginationRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 52px;
`;

const Pagination = styled.div`
  position: absolute;
  bottom: 0;
  right: 15px;
`;

const StyledDataGrid = styled(DataGrid)`
  .MuiTablePagination-root {
    display: none;
  }

  .sticky-left {
    position: sticky;
    left: 0;
    background-color: ${vibrant[900]};
    border-right: 1px solid ${vibrant[700]};
    z-index: 999;
    display: flex;
    align-items: center;
  }

  .sticky-left-header {
    position: sticky;
    left: 0;
    background-color: ${vibrant[900]};
    border-right: 1px solid ${vibrant[700]};
    text-align: center;
    z-index: 1000;
  }

  .sticky-right {
    position: sticky;
    right: 0;
    border-left: 1px solid ${vibrant[700]};
    background-color: ${vibrant[900]};
    z-index: 999;
    display: flex;
    align-items: center;
  }

  .sticky-right-header {
    position: sticky;
    right: 0;
    background-color: ${vibrant[900]};
    border-left: 1px solid ${vibrant[700]};
    text-align: center;
    z-index: 1000;
  }
`;

type PaginatedTableProps<T extends GridValidRowModel> = {
  columns: GridColDef[];
  data: T[];
  rowIdField: keyof T;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: (options?: FetchNextPageOptions) => Promise<unknown>;
  hasNextPage?: boolean;
  pageSizeOptions?: number[];
  height?: number | string;
  datacy?: string;
};

function PaginatedTable<T extends GridValidRowModel>({
  columns,
  data,
  rowIdField,
  isLoading,
  isFetchingNextPage,
  fetchNextPage,
  hasNextPage,
  pageSizeOptions = [10, 30, 50, 100],
  height = 600,
  datacy,
}: PaginatedTableProps<T>) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [displayedRows, setDisplayedRows] = useState<readonly T[]>([]);

  useEffect(() => {
    const startIndex = page * pageSize;
    const endIndex = startIndex + pageSize;
    const rowsWithId = data.map((item) => ({
      ...item,
      //Ensures each row has a unique id (datatable requirement)
      id: item[rowIdField] as string | number,
    }));
    setDisplayedRows(rowsWithId.slice(startIndex, endIndex));
  }, [data, page, pageSize]);

  const handleNextPage = async () => {
    if (hasNextPage) {
      setPage((prev) => prev + 1);
      await fetchNextPage();
    }
  };

  const handlePreviousPage = () => {
    if (page > 0) {
      setPage((prev) => prev - 1);
    }
  };

  const handlePageSizeChange = async (event: SelectChangeEvent<number>) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    setPage(0);
    await fetchNextPage();
  };

  return (
    <PaginationWrapper height={height}>
      {isLoading ? (
        <Skeleton animation="wave" variant="rectangular" height={height} />
      ) : (
        <>
          <StyledDataGrid
            rows={displayedRows}
            rowHeight={36}
            columnHeaderHeight={36}
            columns={columns}
            rowCount={
              hasNextPage
                ? displayedRows.length + pageSize
                : displayedRows.length
            }
            loading={isFetchingNextPage}
            paginationMode="server"
            paginationModel={{ pageSize, page }}
            disableVirtualization
            data-cy={datacy}
          />
          <Pagination>
            <PaginationRow>
              <Select<number>
                value={pageSize}
                onChange={handlePageSizeChange}
                size="small"
                sx={{ height: "38px", minWidth: "70px" }}
              >
                {pageSizeOptions.map((size) => (
                  <MenuItem key={size} value={size} sx={{ color: "#000" }}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
              <Button
                data-cy="previous-page-button"
                onClick={handlePreviousPage}
                disabled={page === 0 || isFetchingNextPage}
                sx={{ color: "#fff!important" }}
              >
                Previous
              </Button>
              <Typography
                variant="body2"
                style={{ margin: "0 8px", textTransform: "uppercase" }}
              >
                Page {page + 1}
              </Typography>
              <Button
                data-cy="next-page-button"
                onClick={handleNextPage}
                disabled={!hasNextPage || isFetchingNextPage}
                sx={{ color: "#fff!important" }}
              >
                Next
              </Button>
            </PaginationRow>
          </Pagination>
        </>
      )}
    </PaginationWrapper>
  );
}

export default PaginatedTable;
